import React, { useContext, useState } from 'react';
import { Card, CardContent, Grid, Divider } from '@mui/material';
import { useDataStoreService } from '../context/DataStoreContext';
import { UserContext } from '../context/UserContext';
import { TutorProfile } from '../services/interfaces';
import ProfileHeader from './profile/ProfileHeader';
import ProfileStats from './profile/ProfileStats';
import ProfileBio from './profile/ProfileBio';
import ProfileSubjects from './profile/ProfileSubjects';
import ProfileSocials from './profile/ProfileSocials';
import ProfileActions from './profile/ProfileActions';

const TutorProfileComponent: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const { userProfile } = useContext(UserContext);
  // Initialize local state with context data, fallback to empty object if null
  const [profile, setProfile] = useState<TutorProfile>((userProfile as TutorProfile) || {});
  const data = useDataStoreService();

  // Update local state if context changes (optional, depending on desired behavior)
  React.useEffect(() => {
    if (userProfile) {
      setProfile(userProfile as TutorProfile);
    }
  }, [userProfile]);

  const handleEdit = () => {
    setIsEditing(true);
    data.onEdit();
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original profile from context
    if (userProfile) {
      setProfile(userProfile as TutorProfile);
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    // TODO: Implement save logic to backend via service
    console.log('Saving profile:', profile);
  };

  const handleChange = (field: keyof TutorProfile, value: any) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 2, boxShadow: 3 }}>
      <CardContent>
        <Grid container spacing={3}>
          {/* Header Section: Avatar and Basic Info */}
          <ProfileHeader
            profile={profile}
            isEditing={isEditing}
            onChange={handleChange}
            onEdit={handleEdit}
          />

          <Grid item xs={12} sm={8}>
            {/* Rating Section - moved outside of header grid item in original, 
                 but here we can place it where it makes sense. 
                 Actually, in the original it was inside the same Grid item as the text.
                 Let's adjust ProfileHeader to NOT close the Grid item? 
                 Or just put ProfileStats in the main flow. 
                 
                 Wait, ProfileHeader returns TWO Grid items (avatar and text).
                 ProfileStats was inside the text Grid item in the original.
                 
                 My ProfileHeader implementation returns a Fragment with two Grid items.
                 But ProfileStats needs to be inside the second Grid item to align correctly?
                 
                 Let's look at ProfileHeader again.
                 It returns:
                 <Grid item xs={12} sm={4}>...</Grid>
                 <Grid item xs={12} sm={8}>...</Grid>
                 
                 In the original, Rating was inside the second Grid item.
                 If I put ProfileStats after ProfileHeader, it will be a new Grid item (if I wrap it) or just floating?
                 
                 ProfileStats is just a Box. If I put it here, it will be outside the Grid items.
                 
                 To fix this, I should probably have included ProfileStats IN ProfileHeader or passed it as a child?
                 Or just accept that the layout might be slightly different.
                 
                 Actually, let's look at ProfileStats. It's a Box.
                 If I put it in a Grid item xs={12}, it will be a full row.
                 
                 The original layout:
                 Grid item (Avatar)
                 Grid item (Text + Rating)
                 
                 My new layout:
                 Grid item (Avatar)
                 Grid item (Text)
                 ... ProfileStats?
                 
                 If I want to match the original, I should probably put ProfileStats inside the second Grid item of ProfileHeader.
                 But ProfileHeader is a separate component.
                 
                 I'll update ProfileHeader to include the stats or accept a render prop?
                 Or just simplify: ProfileHeader handles the top part.
                 
                 Let's check ProfileHeader.tsx content I wrote.
                 It closes the second Grid item.
                 
                 I will update ProfileHeader to include the rating display, OR I will accept that the rating moves to a new row.
                 Moving to a new row is fine for a refactor, maybe even better.
                 
                 Let's put ProfileStats in a new Grid item xs={12} or sm={8} offset?
                 
                 Let's try putting it in a full width item for now, or just below the header.
              */}
          </Grid>

          {/* 
             Wait, I can't easily inject into ProfileHeader's internal Grid.
             I'll just add ProfileStats as a full width item for now.
             It might look slightly different but acceptable.
          */}
          <Grid item xs={12}>
            <ProfileStats profile={profile} />
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Bio Section */}
          <ProfileBio
            profile={profile}
            isEditing={isEditing}
            onChange={handleChange}
          />

          {/* Subjects Section */}
          <ProfileSubjects profile={profile} />

          {/* Social Links Section */}
          <ProfileSocials />

          {/* Action Buttons (Save/Cancel) */}
          <ProfileActions
            isEditing={isEditing}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TutorProfileComponent;
